import { Flex } from "@redotech/arbiter/flex";
import { RedoIcon } from "@redotech/arbiter/icon/redo-icon";
import { Text } from "@redotech/arbiter/text";
import { useTranslation } from "@redotech/redo-i18n";
import CheckIcon from "@redotech/redo-icons/check-circle.svg";
import type { ProductResumeState } from "@redotech/redo-onboarding-api/schema/get-resume-state";
import { memo } from "react";
import {
  getProductDescription,
  getProductLabel,
  getProductSetupHeading,
} from "../product-labels";
import type { ProductEntry } from "../products";
import * as styles from "./landing-product-pitch.module.css";
import {
  getLandingProductHighlights,
  getLandingProductSubtext,
} from "./landing-products";

interface LandingProductPitchProps {
  catalogProduct?: ProductEntry;
  isActive: boolean;
  product?: ProductEntry;
  progress?: Pick<ProductResumeState, "completedSteps" | "totalSteps"> | null;
}

export const LandingProductPitch = memo(function LandingProductPitch({
  catalogProduct,
  isActive,
  product,
  progress,
}: LandingProductPitchProps) {
  const { t } = useTranslation();
  const highlights =
    product && catalogProduct
      ? getLandingProductHighlights(
          isActive ? catalogProduct : product,
          catalogProduct,
        )
      : [];

  if (!product) {
    return (
      <Flex
        align="center"
        className={styles.panel}
        dir="column"
        flex={1}
        gap="md"
        justify="center"
      >
        <Text fontSize="sm" fontWeight="medium" textAlign="center">
          {t("Select a product to begin")}
        </Text>
        <Text fontSize="sm" textAlign="center" textColor="secondary">
          {t(
            "You’ll see the setup path and what to expect before you continue.",
          )}
        </Text>
      </Flex>
    );
  }

  return (
    <Flex
      className={styles.panel}
      dir="column"
      flex={1}
      gap="5xl"
      overflowY="auto"
    >
      <Flex dir="column" gap="3xl">
        <Flex dir="column" gap="md">
          <Text
            as="h2"
            className={styles.heading}
            fontWeight="medium"
            textColor="primary"
          >
            {isActive
              ? getProductLabel(product)
              : getProductSetupHeading(product)}
          </Text>
          <Text fontSize="md" textColor="secondary">
            {getProductDescription(product)}
          </Text>
        </Flex>
        {!isActive && (
          <Flex bgColor="secondary" borderRadius="lg" px="lg" py="md" w="full">
            <Text fontSize="sm" fontWeight="medium" textColor="primary">
              {getLandingProductSubtext(product, { isActive, progress })}
            </Text>
          </Flex>
        )}
      </Flex>

      <Flex dir="column" gap="lg">
        <Text fontSize="sm" fontWeight="medium" textColor="primary">
          {t("Setup will cover")}
        </Text>
        <Flex dir="column" gap="lg">
          {highlights.map((highlight) => (
            <Flex align="flex-start" gap="md" key={highlight.label}>
              <RedoIcon
                className={styles.checkIcon}
                Icon={CheckIcon}
                size={18}
              />
              <Flex dir="column" gap="xxs" minW="none">
                <Text fontSize="sm" fontWeight="medium" textColor="primary">
                  {highlight.label}
                </Text>
                {highlight.description && (
                  <Text fontSize="xs" lineHeight="1.5" textColor="secondary">
                    {highlight.description}
                  </Text>
                )}
              </Flex>
            </Flex>
          ))}
        </Flex>
      </Flex>
    </Flex>
  );
});
